/* eslint-disable max-lines */
import { AgGridAngular } from '@ag-grid-community/angular';
import {
  CellEditingStoppedEvent,
  CellValueChangedEvent,
  ExcelExportParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  ProcessCellForExportParams,
  RowDragEndEvent,
  RowNode,
  ValueSetterParams,
} from '@ag-grid-community/core';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { isEqual, uniq } from 'lodash-es';
import { BehaviorSubject, combineLatest, firstValueFrom, ReplaySubject } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import { PatientCurveQuery } from '@models/patient-curve/patient-curve.query';
import { PatientCurveService } from '@models/patient-curve/patient-curve.service';
import { PatientGroupsModel } from '@models/patient-group/patient-groups.model';
import { PatientGroupsQuery } from '@models/patient-group/patient-groups.query';
import { PatientGroupsService } from '@models/patient-group/patient-groups.service';
import { PatientGroupsStore } from '@models/patient-group/patient-groups.store';

import { AgActionsComponent } from '@shared/ag-components/ag-actions/ag-actions.component';
import {
  AgCheckboxRendererComponent,
  IAgCheckboxParams,
} from '@shared/ag-components/ag-checkbox-rerender/ag-checkbox-renderer.component';
import { ButtonComponent } from '@shared/components/button/button.component';
import { ConfirmationActionModalComponent } from '@shared/components/modals/confirmation-action-modal/confirmation-action-modal.components';
import { ConfirmationModalComponent } from '@shared/components/modals/confirmation-modal/confirmation-modal.component';
import { FormControlConstants } from '@shared/constants/form-control.constants';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ROUTING_PATH } from '@shared/constants/routingPath';
import { TableConstants } from '@shared/constants/table.constants';
import {
  EventType,
  GqlService,
  listPatientGroupsHavingTransactionsQuery,
  Organization,
  PatientGroupType,
} from '@shared/services/gql.service';
import { LaunchDarklyService } from '@shared/services/launch-darkly.service';
import { OverlayService } from '@shared/services/overlay.service';
import { EventService } from '@shared/store/event/event.service';
import { MainQuery } from '@shared/store/main/main.query';
import { OrganizationQuery } from '@shared/store/organization/organization.query';
import { TrialsQuery } from '@shared/store/trials/trials.query';
import { AgSetColumnsVisible } from '@shared/utils';
import { RequireSome, Utils } from '@shared/utils/utils';

import { ExportExcelButtonComponent } from '../export-excel-button/export-excel-button.component';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent, ExportExcelButtonComponent, AgGridAngular, AsyncPipe],
  selector: 'aux-patient-groups',
  templateUrl: './patient-groups.component.html',
})
export class PatientGroupsComponent {
  gqlService = inject(GqlService);
  organizationQuery = inject(OrganizationQuery);
  overlayService = inject(OverlayService);
  patientCurveQuery = inject(PatientCurveQuery);
  patientCurveService = inject(PatientCurveService);
  patientGroupsQuery = inject(PatientGroupsQuery);
  patientGroupsService = inject(PatientGroupsService);

  private changeDetector = inject(ChangeDetectorRef);
  private eventService = inject(EventService);
  private launchDarklyService = inject(LaunchDarklyService);
  private mainQuery = inject(MainQuery);
  private patientGroupsStore = inject(PatientGroupsStore);
  private trialsQuery = inject(TrialsQuery);

  addedRowCounter = 0;
  editedRows = new Set<string>();
  editModeGrid$ = new BehaviorSubject(false);
  excelOptions = {
    author: 'Auxilius',
    columnWidth(params) {
      switch (params.column?.getId()) {
        case 'type':
          return 75;
        default:
          return 225;
      }
    },
    fileName: 'auxilius-patient-group.xlsx',
    fontSize: 11,
    processCellCallback(params: ProcessCellForExportParams): string {
      if (params.column.getColId() === 'curveOnly') {
        return params.value ? 'Yes' : 'No';
      }

      return params.value;
    },
    sheetName: 'Patient Groups',
    shouldRowBeSkipped(params) {
      return !params.node?.data?.id;
    },
  } as ExcelExportParams;
  gridAPI!: GridApi;
  gridAPI$ = new ReplaySubject<GridApi>(1);
  gridData$ = this.patientGroupsQuery.selectAll().pipe(
    map((value) => {
      return value.map(({ curveOnly, description, id, name, rank_order, type }) => ({
        curveOnly,
        description,
        id,
        name,
        rank_order,
        type,
      }));
    })
  );
  gridOptions$: BehaviorSubject<GridOptions> = new BehaviorSubject({
    defaultColDef: {
      ...TableConstants.DEFAULT_GRID_OPTIONS.DEFAULT_COL_DEF,
      editable: () => {
        return this.editModeGrid$.getValue();
      },
      suppressMenu: true,
      suppressMovable: true,
    },
    ...TableConstants.DEFAULT_GRID_OPTIONS.GRID_OPTIONS,
    columnDefs: [
      {
        field: 'id',
        headerName: 'id',
        hide: true,
      },
      {
        ...TableConstants.DEFAULT_GRID_OPTIONS.ACTIONS_COL_DEF,
        cellRenderer: AgActionsComponent,
        cellRendererParams: {
          deleteClickFN: ({ rowNode }: { rowNode: RowNode }) => {
            if (!rowNode.data.id) {
              this.gridAPI.applyTransaction({ remove: [rowNode.data] });

              if (this.addedRowCounter === 1) {
                this.reorderedRows.clear();
                this.removedRows = [];
                this.newRowAdded = false;
              } else {
                this.removedRows.pop();
              }

              this.addedRowCounter -= 1;
              this.checkChanges();
            } else {
              void this.dialogConfirmRemovePatientGroup(rowNode);
            }
          },
        },
        hide: true,
        rowDrag: true,
      },
      {
        cellClass: 'text-left',
        cellClassRules: {
          'border-aux-error': (params) => params.data.showError,
        },
        field: 'name',
        headerName: 'Patient Group',
        tooltipValueGetter: Utils.getDuplicateNameCellTooltip,
        valueSetter: (params: ValueSetterParams) => {
          params.data.name = params.newValue?.toString().trim() || '';

          return true;
        },
      },
      {
        cellClass: 'text-left',
        field: 'description',
        headerName: 'Description',
      },
      {
        cellRenderer: AgCheckboxRendererComponent,
        cellRendererParams: <IAgCheckboxParams>{
          getDisabledState: (params) => {
            if (!this.editModeGrid$.getValue()) {
              return true;
            }

            if (
              params.node.data.id &&
              this.listPatientGroupsHavingTransactions.find((x) => x.id === params.node.data.id)
            ) {
              return true;
            }

            return false;
          },
          tooltipFN: (params) => {
            if (
              params.node.data.id &&
              this.listPatientGroupsHavingTransactions.find((x) => x.id === params.node.data.id) &&
              this.editModeGrid$.getValue()
            ) {
              return 'Delete associated Investigator Transactions to change to Curve Only.';
            }

            return '';
          },
        },
        editable: false,
        field: 'curveOnly',
        headerName: 'Curve Only',
        maxWidth: 70,
        minWidth: 70,
        suppressFillHandle: true,
      },
    ],
    enableRangeSelection: true,
    enterNavigatesVertically: true,
    enterNavigatesVerticallyAfterEdit: true,
    excelStyles: [
      {
        font: { bold: true, color: '#FFFFFF', fontName: 'Arial', size: 11 },
        id: 'header',
        interior: { color: '#094673', pattern: 'Solid', patternColor: '#094673' },
      },
      {
        font: { color: '#D73C37' },
        id: 'text-aux-error',
      },
      {
        font: { color: '#437F7F' },
        id: 'text-aux-green',
      },
      {
        font: { bold: true, color: '#FFFFFF', fontName: 'Arial', size: 11 },
        id: 'first_row',
        interior: { color: '#999999', pattern: 'Solid', patternColor: '#999999' },
      },
    ],
    getRowClass: Utils.oddEvenRowClass,
    getRowId: ({ data }) => data?.id || data?.randomID,
    rowDragManaged: true,
    rowSelection: 'multiple',
    suppressCellFocus: false,
    suppressCopyRowsToClipboard: true,
    suppressMenuHide: true,
    undoRedoCellEditing: true,
    undoRedoCellEditingLimit: 20,
  } as GridOptions);
  hasChanges = false;
  initialValues: PatientGroupsModel[] = [];
  listPatientGroupsHavingTransactions: listPatientGroupsHavingTransactionsQuery[] = [];
  loading$ = this.patientGroupsQuery.selectLoading();
  newRowAdded = false;
  removedRows: PatientGroupsModel[] = [];
  reorderedRows = new Set<string>();
  reorderQueue: { ids: string; new_index: number }[] = [];
  valuesBeforeRemove: PatientGroupsModel[] = [];

  cancelEditMode(): void {
    this.gridAPI?.stopEditing();
    this.editModeGrid$.next(false);

    if (this.gridAPI) {
      AgSetColumnsVisible({
        gridApi: this.gridAPI,
        keys: [TableConstants.FIELDS.ACTIONS],
        visible: false,
      });
    }

    this.sizeColumnsToFit();
    this.gridData$ = this.patientGroupsQuery.selectAll().pipe(
      map((value) => {
        return value.map(({ curveOnly, description, id, name, rank_order, type }) => ({
          curveOnly,
          description,
          id,
          name,
          rank_order,
          type,
        }));
      })
    );
    this.resetChangeIndicators();
    this.gridAPI.refreshCells({ force: true });
  }

  async canDeactivate(): Promise<boolean> {
    if (!this.editModeGrid$.getValue()) {
      return true;
    }

    this.checkChanges();

    if (this.hasChanges) {
      const result = this.overlayService.openUnsavedChangesConfirmation();
      const event = await firstValueFrom(result.afterClosed$);

      return !!event.data;
    }

    return true;
  }

  cellEditingStopped(event: CellEditingStoppedEvent): void {
    if (!event.newValue?.trim()) {
      this.getRowsErrors();
      this.gridAPI.redrawRows();
    }
  }

  cellValueChanged(event: CellValueChangedEvent) {
    if (event.data.id) {
      this.editedRows.add(event.data.id);
      this.checkChanges();
    }

    this.getRowsErrors();
    this.gridAPI.redrawRows();
  }

  private checkChanges(): void {
    const currentValues = this.getCurrentValues();

    this.hasChanges = !isEqual(this.initialValues, currentValues);

    if (this.hasChanges) {
      this.changeDetector.markForCheck();
    }
  }

  constructor() {
    this.gqlService
      .listPatientGroupsHavingTransactions$()
      .pipe(takeUntilDestroyed())
      .subscribe(({ data }) => {
        this.listPatientGroupsHavingTransactions = data || [];
      });

    this.patientGroupsService.get().pipe(takeUntilDestroyed()).subscribe();
    this.patientCurveService.get().pipe(takeUntilDestroyed()).subscribe();

    this.gridData$.pipe(takeUntilDestroyed()).subscribe((gridData) => {
      this.initialValues = gridData;

      if (this.valuesBeforeRemove.length) {
        setTimeout(() => {
          this.gridAPI?.setGridOption('rowData', this.valuesBeforeRemove);
          this.valuesBeforeRemove = [];
        }, 0);
      }

      if (!this.hasChanges) {
        this.gridAPI?.setGridOption('rowData', gridData);
      }

      this.checkChanges();
    });

    this.gridAPI$
      .pipe(
        take(1),
        switchMap(() =>
          this.launchDarklyService.select$((flags) => flags.patient_groups_curve_only)
        )
      )
      .pipe(takeUntilDestroyed())
      .subscribe((bool) => {
        AgSetColumnsVisible({
          gridApi: this.gridAPI,
          keys: ['curveOnly'],
          visible: bool,
        });
      });

    combineLatest([
      this.eventService.select$(EventType.TRIAL_CHANGED),
      this.mainQuery.select('trialKey'),
    ])
      .pipe(takeUntilDestroyed())
      .subscribe(() => {
        this.initialValues = [];
        this.cancelEditMode();
      });
  }

  // eslint-disable-next-line max-lines-per-function
  private dialogConfirmRemovePatientGroup(rowNode: RowNode) {
    const patient = this.patientCurveQuery.getAll();
    const curves = patient.filter((el) => el.patient_group_id === rowNode.data.id);

    const blendedCurves = patient.filter((el) =>
      el.constituent_patient_groups.find((c) => c.id === rowNode.data.id)
    );

    const organizations_with_forecasts = [...curves, ...blendedCurves].reduce(
      (acc: Organization[], el) => {
        const organizations = el.organizations_with_forecasts;

        organizations.forEach((org) => {
          if (!acc.find((e) => e.id === org.id)) {
            acc.push(org);
          }
        });

        return acc;
      },
      []
    );

    const doesHaveTransactions = this.listPatientGroupsHavingTransactions.find(
      (x) => x.id === rowNode.data.id
    );

    if (!curves.length && !blendedCurves.length && !doesHaveTransactions) {
      this.overlayService.openPopup<
        { message: string },
        boolean | undefined,
        ConfirmationModalComponent
      >({
        content: ConfirmationModalComponent,
        data: {
          message: `Are you sure you want to remove ${rowNode.data.name}?`,
        },
        settings: {
          header: 'Remove Patient Group',
          primaryButton: {
            action: (instance) => {
              this.removedRows.push(rowNode.data);
              this.gridAPI.applyTransaction({ remove: [rowNode.data] });
              this.valuesBeforeRemove = this.getCurrentValues();
              this.checkChanges();
              instance?.ref.close();
            },
            label: 'Remove',
          },
        },
      });

      return;
    }

    let list = doesHaveTransactions
      ? `<br>Delete associated Investigator Transactions to Delete Patient Group.<br>`
      : ``;

    if (curves.length) {
      list += `<br>Patient Curves:<br><ul class="list-disc pl-4">${curves
        .map(
          ({ name }) =>
            `<li><a class="aux-link" href="/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.PATIENT_DRIVER.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.PATIENT_DRIVER.CURVES}">${name}</a></li>`
        )
        .join('')}${blendedCurves
        .map(
          ({ name }) =>
            `<li><a class="aux-link"  href="/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.PATIENT_DRIVER.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.DRIVERS.PATIENT_DRIVER.CURVES}">${name}</a></li>`
        )
        .join('')}</ul>`;
    }

    if (organizations_with_forecasts.length) {
      list += `<br>Forecast Methodology:<br><ul class="list-disc pl-4">${organizations_with_forecasts
        .map(
          ({ name }) =>
            `<li><a class="aux-link" href="/${ROUTING_PATH.FORECAST_ROUTING.INDEX}/${ROUTING_PATH.FORECAST_ROUTING.FORECAST_METHODOLOGY}">${name}</a></li>`
        )
        .join('')}</ul>`;
    }

    const warningForDependencies =
      curves.length || organizations_with_forecasts.length
        ? `<br>Please first remove the dependencies below and then ${rowNode.data.name} can be successfully deleted.<br>`
        : '';

    this.overlayService.openPopup({
      data: {
        hideConfirmationHint: true,
        message: `${rowNode.data.name} cannot be deleted because it is being used in other areas of the application.<br>${warningForDependencies}${list}`,
        skipKeywordConfirmation: true,
      },
      modal: ConfirmationActionModalComponent,
      settings: {
        header: 'Remove Patient Group',
        primaryButton: {
          disabled: true,
          label: 'Remove',
        },
      },
    });
  }

  editGrid(): void {
    this.editModeGrid$.next(true);

    if (this.gridAPI) {
      AgSetColumnsVisible({
        gridApi: this.gridAPI,
        keys: [TableConstants.FIELDS.ACTIONS],
        visible: true,
      });
    }

    this.sizeColumnsToFit();
    this.gridAPI.redrawRows();
    this.gridAPI.startEditingCell({
      colKey: 'name',
      rowIndex: 0,
    });
  }

  private getCurrentValues(): PatientGroupsModel[] {
    const currentValues: PatientGroupsModel[] = [];

    this.gridAPI?.forEachNode(({ data }) => {
      currentValues.push({
        ...data,
        description: data.description === '' ? null : data.description,
      });
    });

    return currentValues;
  }

  getDynamicExcelParams(): ExcelExportParams {
    const trial = this.trialsQuery.getEntity(this.mainQuery.getValue().trialKey);

    if (!trial) {
      return {};
    }

    return {
      ...this.excelOptions,
      columnKeys: ['name', 'description', 'curveOnly'],
      prependContent: [
        {
          cells: [
            {
              data: { type: 'String', value: `Trial: ${trial.short_name}` },
              mergeAcross: 2,
              styleId: 'first_row',
            },
          ],
        },
      ],
    } as ExcelExportParams;
  }

  private getRowsErrors(): string[] {
    const errors: string[] = [];
    const uniqueNames = this.gridUniqueNames();

    this.gridAPI.forEachNode((node) => {
      const rowErrors = [];
      const { name } = node.data as PatientGroupsModel;
      const trimmedName = name?.trim();

      if (!trimmedName) {
        rowErrors.push(FormControlConstants.VALIDATION_MESSAGE.REQUIRED_PATIENT_GROUP);
      }

      if (trimmedName && !uniqueNames.includes(trimmedName.toLowerCase())) {
        rowErrors.push(FormControlConstants.VALIDATION_MESSAGE.DUPLICATED_PATIENT_GROUP);
      }

      node.data.showError = !!rowErrors.length;
      errors.push(...rowErrors);
    });

    return uniq(errors);
  }

  private gridUniqueNames(): string[] {
    const arr: string[] = [];

    this.gridAPI.forEachNode((node) => {
      arr.push(node.data?.name?.trim().toLowerCase() || '');
    });

    return arr.filter((el) => arr.indexOf(el) === arr.lastIndexOf(el));
  }

  onAddPatientGroups() {
    const ss = this.gridAPI.applyTransaction({
      add: [{ randomID: Utils.uuid() }],
    });

    if (ss?.add.length && ss.add[0].rowIndex) {
      this.gridAPI.startEditingCell({
        colKey: 'name',
        rowIndex: ss.add[0].rowIndex,
      });
    }

    this.addedRowCounter += 1;
    this.newRowAdded = true;
    this.checkChanges();
  }

  onGridReady({ api }: GridReadyEvent) {
    this.gridAPI = api;
    this.gridAPI$.next(api);
    api.sizeColumnsToFit();
    Utils.updateGridLayout(this.gridAPI, 'patientGroupsGrid');

    this.initialValues = this.getCurrentValues();
  }

  onRowDragEnd(event: RowDragEndEvent) {
    if (event.node.data?.id && event.node.rowIndex !== null) {
      this.reorderQueue.push({
        ids: event.node.data.id,
        new_index: event.node.rowIndex + 1,
      });
      this.reorderedRows.add(event.node.data.id);
      this.checkChanges();
    }
  }

  onSaveAll = async () => {
    if (this.getRowsErrors().length) {
      this.overlayService.error(this.getRowsErrors());
      this.gridAPI.redrawRows();

      return;
    }

    this.patientGroupsStore.setLoading(true);

    if (await this.savePatientGroups()) {
      this.overlayService.success(MessagesConstants.SUCCESSFULLY_SAVED);
    } else {
      this.overlayService.error(MessagesConstants.ERROR_OCCURRED);
    }

    this.patientGroupsStore.setLoading(false);
    this.cancelEditMode();
  };

  // eslint-disable-next-line max-lines-per-function
  private parseChanges(): {
    insertData: PatientGroupsModel[];
    reorderData: PatientGroupsModel[];
    updateData: PatientGroupsModel[];
  } {
    const updateData: PatientGroupsModel[] = [];
    const reorderData: PatientGroupsModel[] = [];
    const insertData: PatientGroupsModel[] = [];

    if (
      this.reorderQueue.length < this.gridAPI.getDisplayedRowCount() &&
      !this.removedRows.length
    ) {
      this.reorderQueue.forEach(({ ids, new_index }) => {
        const pp = this.patientGroupsQuery.getEntity(ids);

        if (pp) {
          const { curveOnly, description, id, name, type } = pp;
          let dataType = type;

          if (type !== PatientGroupType.PATIENT_GROUP_BLENDED) {
            dataType = curveOnly
              ? PatientGroupType.PATIENT_GROUP_FORECAST_ONLY
              : PatientGroupType.PATIENT_GROUP_STANDARD;
          }

          updateData.push({
            description,
            id,
            name,
            rank_order: new_index,
            type: dataType,
          });
        }
      });
      this.gridAPI.forEachNode((node) => {
        const rowIndex = (node.rowIndex || 0) + 1;

        const { curveOnly, description, id, name, type } = node.data as RequireSome<
          PatientGroupsModel,
          'description' | 'id' | 'name' | 'rank_order' | 'type'
        >;

        let dataType = type;

        if (type !== PatientGroupType.PATIENT_GROUP_BLENDED) {
          dataType = curveOnly
            ? PatientGroupType.PATIENT_GROUP_FORECAST_ONLY
            : PatientGroupType.PATIENT_GROUP_STANDARD;
        }

        if (!id && !this.editedRows.has(id)) {
          insertData.push({
            description,
            id,
            name,
            rank_order: rowIndex,
            type: dataType,
          });
        }

        if (id && this.editedRows.has(id)) {
          updateData.push({
            description,
            id,
            name,
            rank_order: rowIndex,
            type: dataType,
          });
        }

        if (id) {
          reorderData.push({
            description,
            id,
            name,
            rank_order: rowIndex,
            type: dataType,
          });
        }
      });
    } else {
      this.gridAPI.forEachNode((node) => {
        const rowIndex = (node.rowIndex || 0) + 1;
        const { curveOnly, description, id, name, type } = node.data as RequireSome<
          PatientGroupsModel,
          'description' | 'id' | 'name' | 'rank_order' | 'type'
        >;

        let dataType = type;

        if (type !== PatientGroupType.PATIENT_GROUP_BLENDED) {
          dataType = curveOnly
            ? PatientGroupType.PATIENT_GROUP_FORECAST_ONLY
            : PatientGroupType.PATIENT_GROUP_STANDARD;
        }

        if (this.reorderQueue.length || !id || this.editedRows.has(id) || this.removedRows.length) {
          updateData.push({
            description,
            id,
            name,
            rank_order: rowIndex,
            type: dataType,
          });
        }

        if (!id && !this.editedRows.has(id)) {
          insertData.push({
            description,
            id,
            name,
            rank_order: rowIndex,
            type: dataType,
          });
        }

        if (id) {
          reorderData.push({
            description,
            id,
            name,
            rank_order: rowIndex,
            type: dataType,
          });
        }
      });
    }

    return { insertData, reorderData, updateData };
  }

  private resetChangeIndicators(): void {
    this.reorderedRows.clear();
    this.editedRows.clear();
    this.removedRows = [];
    this.reorderQueue = [];
    this.newRowAdded = false;
    this.addedRowCounter = 0;
    this.hasChanges = false;
    this.valuesBeforeRemove = [];
  }

  private async savePatientGroups(): Promise<boolean> {
    let errorList: string[] = [];
    const { insertData, reorderData, updateData } = this.parseChanges();

    if (this.removedRows.length) {
      for (const { description, id, name, rank_order, type } of this.removedRows) {
        errorList = errorList.concat(
          (await this.patientGroupsService.remove({ description, id, name, rank_order, type }))
            .errors
        );
      }
    }

    if (updateData.length) {
      for (const { description, id, name, rank_order, type } of reorderData) {
        errorList = errorList.concat(
          (await this.patientGroupsService.update({ description, id, name, rank_order, type }))
            .errors
        );
      }
    }

    if (insertData.length) {
      for (const pg of insertData) {
        errorList = errorList.concat((await this.patientGroupsService.add(pg)).errors);
      }
    }

    return !errorList.length;
  }

  sizeColumnsToFit(): void {
    this.gridAPI.sizeColumnsToFit();
  }
}
